import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Facebook, ShoppingCart, BookOpen, Store, BookMarked } from "lucide-react";
import { BOOK, getSaleState } from "@/lib/site";
import WaitlistForm from "./WaitlistForm";
import Starfield from "./Starfield";

export default function Footer({ minimal = false }) {
  const isPromo = getSaleState() === "launchweek";
  return (
    <footer id="contact" className={`relative z-10 overflow-hidden border-t border-white/10 ${minimal ? "pt-14 md:pt-16" : "pt-24 md:pt-32"}`}>
      <Starfield count={40} />
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        {!minimal && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="text-3xl text-gold">✦</span>
          <h2 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-bold leading-tight text-cream md:text-6xl">
            Join the Journey
          </h2>
          <p className="mx-auto mt-5 max-w-xl font-body text-lg text-slate-300">
            Grab your copy of <em className="not-italic text-cream">{BOOK.title}</em> today, or join
            the mailing list to be the first to know about Book 2.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={BOOK.amazon}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-buy-amazon"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-medium text-twilight transition-[background-color,box-shadow] duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(251,191,36,0.55)]"
            >
              <ShoppingCart className="h-4 w-4" /> Buy on Amazon
            </a>
            <a
              href={BOOK.barnesNoble}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-buy-bn"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color] duration-300 hover:bg-gold hover:text-twilight"
            >
              <BookOpen className="h-4 w-4" /> Barnes &amp; Noble
            </a>
            <a
              href={BOOK.walmart}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-buy-walmart"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color] duration-300 hover:bg-gold hover:text-twilight"
            >
              <Store className="h-4 w-4" /> Walmart
            </a>
            <a
              href={BOOK.kharis}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-buy-kharis"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition-[background-color] duration-300 hover:bg-gold hover:text-twilight"
            >
              <BookMarked className="h-4 w-4" /> Kharis Publishing
            </a>
          </div>
          <div className="mt-9 flex justify-center">
            <WaitlistForm variant="footer" />
          </div>
        </motion.div>
        )}

        <div className={`flex flex-col items-center justify-between gap-8 border-t border-white/10 py-10 md:flex-row ${minimal ? "mt-0 border-t-0" : "mt-24"}`}>
          <div className="max-w-md text-center md:text-left">
            <p className="font-display text-lg font-semibold text-cream">{BOOK.brand}</p>
            <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">
              {BOOK.tagline}
            </p>
            <p className="mt-3 font-body text-xs text-slate-500">
              {BOOK.title} by {BOOK.author} · {BOOK.publisher}
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <a
              href={BOOK.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              <Facebook className="h-4 w-4" />
              Follow on Facebook
            </a>
            <a
              href={`mailto:${BOOK.email}`}
              data-testid="footer-email-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 font-body text-sm text-cream transition-[border-color,color] duration-300 hover:border-gold hover:text-gold"
            >
              <Mail className="h-4 w-4" />
              {BOOK.email}
            </a>
          </div>
        </div>
        <p className="pb-8 text-center font-body text-xs text-slate-500">
          <Link
            to="/admin"
            data-testid="secret-admin-link"
            aria-label="Owner dashboard"
            title="Owner dashboard"
            className="cursor-default no-underline text-slate-500 transition-colors duration-300 hover:text-slate-500"
          >
            ©
          </Link>{" "}
          {new Date().getFullYear()} {BOOK.brand}. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
